// Scaling library — parses "3 berenjenas", "100 ml de aceite", "¼ cucharadita
// de cúrcuma", "1 ½ tazas de harina", "1,1 kg", "2 cucharadas" y devuelve la
// cantidad numérica, la unidad y el resto del texto. Sirve pa' recalcular
// proporcionalmente cuando el usuario cambia porciones o escala por un
// ingrediente clave (ej. harina en una masa).
//
// Filosofía: si NO podemos parsear la cantidad (ej. "un poco", "al gusto",
// "1 pizca"), dejamos la línea intacta — no queremos romper recetas.

// Mapa de fracciones unicode → valor decimal
const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 1 / 3, "⅔": 2 / 3,
  "⅕": 0.2, "⅖": 0.4, "⅗": 0.6, "⅘": 0.8,
  "⅙": 1 / 6, "⅚": 5 / 6,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};

// Unidades reconocidas — al matchear una de estas después del número,
// la incluimos en el "prefijo cuantitativo" (queda al lado del número
// escalado). Si no hay unidad, el número se pega directo al nombre.
const UNITS_RE = /^(kilos?|kilogramos?|kgs?|gramos?|grs?|g|miligramos?|mgs?|mg|litros?|lts?|l|mililitros?|mls?|ml|centilitros?|cls?|cl|dl|decilitros?|cucharadas?|cdas?|cucharaditas?|cditas?|tazas?|vasos?|pizcas?|pu[ñn]ados?|unidades?|u\.?|dientes?|hojas?|ramas?|ramitas?|manojos?|cabezas?|tarros?|latas?|paquetes?|piezas?)\b\.?/i;

// Regex principal: al INICIO del texto, captura:
//   Grupo 1: cantidad numérica (soporta "1 ½", "1,5", "1.5", "3/4", "¼", "3")
//   Resto: unidad opcional + resto del texto.
// Nota: el separador decimal en Chile es coma; también aceptamos punto.
const QTY_RE = /^\s*(\d+\s+[¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]|[¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]|\d+[/⁄]\d+|\d+[.,]\d+|\d+)\s*/;

function parseQty(str) {
  // Fracción unicode sola
  if (UNICODE_FRACTIONS[str]) return UNICODE_FRACTIONS[str];
  // Mixto: "1 ½"
  const mixed = str.match(/^(\d+)\s+([¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])$/);
  if (mixed) return parseInt(mixed[1], 10) + UNICODE_FRACTIONS[mixed[2]];
  // Fracción tipo "3/4"
  const frac = str.match(/^(\d+)[/⁄](\d+)$/);
  if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
  // Decimal con coma o punto
  if (/^\d+[.,]\d+$/.test(str)) return parseFloat(str.replace(",", "."));
  // Entero
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  return null;
}

// Formateo de vuelta a texto — devuelve "2", "1,5", "½", "1 ½", etc.
// Prefiere fracciones unicode pa' 1/2, 1/3, 1/4, 3/4 y sus variantes;
// resto en decimales con máximo 2 decimales, coma como separador.
function formatQty(value) {
  if (value == null || !isFinite(value) || value <= 0) return String(value);

  const round = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;
  const intPart = Math.floor(value);
  const frac = round(value - intPart, 4);

  // Tabla inversa: fracciones que "quedan bien" en texto
  const FRAC_TABLE = [
    { val: 0.25, sym: "¼" },
    { val: 0.5,  sym: "½" },
    { val: 0.75, sym: "¾" },
    { val: 0.333, sym: "⅓" },  // 1/3
    { val: 0.667, sym: "⅔" },  // 2/3
    { val: 0.125, sym: "⅛" },
    { val: 0.375, sym: "⅜" },
    { val: 0.625, sym: "⅝" },
    { val: 0.875, sym: "⅞" },
  ];
  const matched = FRAC_TABLE.find((f) => Math.abs(frac - f.val) < 0.02);
  if (matched) {
    if (intPart === 0) return matched.sym;
    return `${intPart} ${matched.sym}`;
  }
  if (frac === 0) return String(intPart);
  // Decimal con coma
  const dec = round(value, 2);
  // Evita ".0" y ".00"
  if (Number.isInteger(dec)) return String(dec);
  return String(dec).replace(".", ",");
}

// Parsea una línea de ingrediente. Devuelve:
//   { qty, unit, rest, orig }  — si se pudo parsear la cantidad
//   { orig, unparseable: true } — si NO se pudo (línea intacta)
export function parseIngredient(line) {
  const orig = String(line);
  const m = orig.match(QTY_RE);
  if (!m) return { orig, unparseable: true };
  const qty = parseQty(m[1].trim());
  if (qty == null) return { orig, unparseable: true };
  let remainder = orig.slice(m[0].length);

  // Detectar unidad (opcional)
  const um = remainder.match(UNITS_RE);
  let unit = "";
  if (um) {
    unit = um[0].trim();
    remainder = remainder.slice(um[0].length);
    // A veces sigue "de" (ej. "100 ml de aceite") — lo mantenemos con el resto
  }
  const rest = remainder.replace(/^\s+/, "");
  return { qty, unit, rest, orig };
}

// Aplica un factor a una línea. Si es unparseable, devuelve la línea tal cual.
export function scaleIngredient(line, factor) {
  const p = parseIngredient(line);
  if (p.unparseable) return p.orig;
  if (factor === 1) return p.orig;
  const newQty = p.qty * factor;
  const qtyText = formatQty(newQty);
  const unit = p.unit ? ` ${p.unit}` : "";
  const rest = p.rest ? ` ${p.rest}` : "";
  return `${qtyText}${unit}${rest}`;
}

// Escala una lista completa de ingredientes.
export function scaleIngredients(ingredients, factor) {
  return (ingredients || []).map((i) => {
    const line = typeof i === "string" ? i : (i?.nombre || JSON.stringify(i));
    return scaleIngredient(line, factor);
  });
}

// Extrae el número de porciones del string. Soporta "4", "4 personas",
// "4-6 personas" (usa el promedio), "para 8". Fallback: 4.
export function parsePorciones(porcionesStr) {
  if (!porcionesStr) return 4;
  const s = String(porcionesStr);
  const range = s.match(/(\d+)\s*[-–a]\s*(\d+)/);
  if (range) return (parseInt(range[1], 10) + parseInt(range[2], 10)) / 2;
  const single = s.match(/\d+/);
  return single ? parseInt(single[0], 10) : 4;
}
